import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, Chip, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface EmailListEditorProps {
    value: string;
    onChange: (value: string) => void;
    sx?: any;
    helperText?: string;
    error?: boolean;
    errorMessage?: string;
    missingField?: boolean;
    label?: string;
    theme?: 'admin' | 'onboarder';
}

const EmailListEditor: React.FC<EmailListEditorProps> = ({
    value,
    onChange,
    sx,
    helperText,
    error,
    errorMessage,
    missingField = false,
    label = 'Contact Email Addresses',
    theme = 'onboarder'
}) => {
    const [emails, setEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    // Convert comma-separated string to array on mount and when value changes
    useEffect(() => {
        if (value) {
            const emailArray = value.split(',').map(email => email.trim()).filter(email => email.length > 0);
            setEmails(emailArray);
        } else {
            setEmails([]);
        }
    }, [value]);

    // Email validation
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Add new email
    const handleAddEmail = () => {
        const trimmedEmail = newEmail.trim().toLowerCase();

        if (!trimmedEmail) {
            setEmailError('Please enter an email address');
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        if (emails.includes(trimmedEmail)) {
            setEmailError('This email is already in the list');
            return;
        }

        const updatedEmails = [...emails, trimmedEmail];
        setEmails(updatedEmails);
        setNewEmail('');
        setEmailError('');

        // Convert back to comma-separated string
        onChange(updatedEmails.join(','));
    };

    // Remove email
    const handleRemoveEmail = (emailToRemove: string) => {
        const updatedEmails = emails.filter(email => email !== emailToRemove);
        setEmails(updatedEmails);

        // Convert back to comma-separated string
        onChange(updatedEmails.join(','));
    };

    // Handle key press for adding email on Enter
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    // Theme-specific styles
    const getFieldSx = () => {
        if (theme === 'admin') {
            return {
                '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.7)' },
                    '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                },
                '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                    color: '#ffffff',
                },
                '& .MuiInputBase-input': {
                    color: '#ffffff',
                },
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '4px',
            };
        }

        // Onboarder theme styles
        return missingField ? {
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(224, 177, 241, 0.15)',
                '&:hover': {
                    backgroundColor: 'rgba(224, 177, 241, 0.2)',
                },
                '&.Mui-focused': {
                    backgroundColor: 'rgba(224, 177, 241, 0.2)',
                }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(224, 177, 241, 0.7)',
                borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(224, 177, 241, 0.9)',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(224, 177, 241, 1)',
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(224, 177, 241, 0.9)',
                fontWeight: 'bold',
            },
            '& .MuiInputBase-input': {
                color: 'rgba(255, 255, 255, 0.9)',
            }
        } : {
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d1c4e9',
            },
            '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputBase-input': {
                color: 'rgba(255, 255, 255, 0.9)',
            }
        };
    };

    const getIconButtonSx = () => {
        if (theme === 'admin') {
            return {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                },
                mt: 0.5
            };
        }

        return {
            backgroundColor: missingField ? 'rgba(224, 177, 241, 0.2)' : 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            '&:hover': {
                backgroundColor: missingField ? 'rgba(224, 177, 241, 0.3)' : 'rgba(255, 255, 255, 0.25)',
            },
            mt: 0.5
        };
    };

    const getChipSx = () => {
        if (theme === 'admin') {
            return {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                        color: 'white'
                    }
                }
            };
        }

        return {
            backgroundColor: missingField ? 'rgba(224, 177, 241, 0.2)' : 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            '& .MuiChip-deleteIcon': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                    color: 'white'
                }
            }
        };
    };

    const getLabelColor = () => {
        if (theme === 'admin') {
            return 'rgba(255, 255, 255, 0.7)';
        }
        return missingField ? 'rgba(224, 177, 241, 0.9)' : 'rgba(255, 255, 255, 0.7)';
    };

    const getHelperTextColor = () => {
        if (theme === 'admin') {
            return emailError ? 'error.main' : 'rgba(255, 255, 255, 0.7)';
        }
        return emailError ? 'error.main' : missingField ? '#C0C0DD' : 'lightgray';
    };

    return (
        <Box sx={sx}>
            <Typography variant="body2" sx={{
                color: getLabelColor(),
                mb: 1,
                fontWeight: missingField ? 'bold' : 'normal'
            }}>
                {label} {missingField && '(Required)'}
            </Typography>

            {/* Display existing emails as chips */}
            {emails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {emails.map((email, index) => (
                        <Chip
                            key={index}
                            label={email}
                            onDelete={() => handleRemoveEmail(email)}
                            deleteIcon={<DeleteIcon />}
                            sx={getChipSx()}
                        />
                    ))}
                </Box>
            )}

            {/* Input for new email */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    fullWidth
                    value={newEmail}
                    onChange={(e) => {
                        setNewEmail(e.target.value);
                        setEmailError('');
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter email address"
                    error={!!emailError}
                    helperText={emailError || helperText}
                    FormHelperTextProps={{
                        sx: {
                            color: getHelperTextColor(),
                            fontWeight: missingField ? 'medium' : 'normal'
                        }
                    }}
                    sx={getFieldSx()}
                />
                <IconButton
                    onClick={handleAddEmail}
                    sx={getIconButtonSx()}
                >
                    <AddIcon />
                </IconButton>
            </Box>

            {error && errorMessage && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default EmailListEditor; 